import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaRocket, FaChartLine, FaCode, FaDatabase, FaUsers, FaHandshake, FaLightbulb, FaAward, FaHeartbeat, FaCreditCard, FaTruck, FaGraduationCap, FaHome, FaShoppingBag, FaTrendUp } from 'react-icons/fa';
import { TrendingUp } from 'lucide-react';

const HomepageContent = () => {
  return (
    <>
      {/* Introduction Section — Mercury Design System */}
      <section className="relative overflow-hidden mercury-hero-gradient mercury-grain">
        {/* Subtle ambient orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ opacity: [0.12, 0.22, 0.12], scale: [1, 1.08, 1] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(201,169,154,0.18) 0%, transparent 70%)' }}
          />
          <motion.div
            animate={{ opacity: [0.10, 0.20, 0.10], scale: [1, 1.06, 1] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 3 }}
            className="absolute -bottom-20 right-0 w-[400px] h-[400px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(183,182,206,0.18) 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative z-10 grid lg:grid-cols-2 min-h-[85vh]">
          {/* Left: Content */}
          <div className="flex items-center px-8 sm:px-12 lg:px-16 xl:px-20 py-16 lg:py-20">
            <div className="max-w-lg w-full">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 mb-8"
                aria-label="Digital Transformation"
              >
                <span className="text-mercury-on-tertiary" aria-hidden="true">+</span>
                <span
                  className="text-xs font-medium tracking-[0.2em] uppercase"
                  style={{ color: '#8A8C99' }}
                >
                  Digital Transformation
                </span>
                <span className="text-mercury-on-tertiary" aria-hidden="true">+</span>
              </motion.div>

              {/* Headline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <h2
                  className="leading-[1.05] tracking-tight mb-2"
                  style={{ fontWeight: 300, fontSize: 'clamp(3rem, 5.5vw, 5.25rem)', color: '#C4C6D0' }}
                >
                  Transforming
                </h2>
                <h2
                  className="leading-[1.05] tracking-tight"
                  style={{ fontWeight: 700, fontSize: 'clamp(3rem, 5.5vw, 5.25rem)', color: '#FAFAFC' }}
                >
                  Businesses
                </h2>
                <p
                  className="mt-1 mb-6"
                  style={{ fontWeight: 400, fontSize: 'clamp(1.1rem, 1.6vw, 1.375rem)', color: '#FAFAFC' }}
                >
                  Through{' '}
                  <span style={{ color: '#5266EB', fontWeight: 500 }}>Innovation</span>
                </p>
              </motion.div>

              {/* Body */}
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-base leading-relaxed mb-8"
                style={{ color: '#C4C6D0', fontWeight: 400 }}
              >
                At Cyborg Sapient, we operate at the intersection of technology and creative performance — helping scaling businesses leverage AI automation, custom engineering, and data-driven campaigns.
              </motion.p>

              {/* Tags / Pills */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-wrap gap-3 mb-10"
              >
                {['AI-Powered Pipeline', 'Telemetry-Driven', 'Results Oriented'].map((tag, i) => (
                  <span
                    key={i}
                    className="px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200"
                    style={{
                      border: '1px solid #2A2A38',
                      color: '#C4C6D0',
                      background: 'transparent',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </motion.div>

              {/* Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="inline-flex items-center gap-4 rounded-xl px-5 py-4"
                style={{
                  background: '#1D1D29',
                  border: '1px solid #2A2A38',
                }}
              >
                <div
                  className="flex items-center justify-center w-9 h-9 rounded-lg"
                  style={{ background: 'rgba(82,102,235,0.15)' }}
                >
                  <TrendingUp size={18} color="#5266EB" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xl font-semibold" style={{ color: '#FAFAFC' }}>500+</div>
                  <div className="text-xs tracking-wider uppercase" style={{ color: '#8A8C99' }}>
                    Projects Completed
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }}
            className="relative overflow-hidden lg:min-h-full min-h-[55vw]"
          >
            <img
              src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=1400&h=1000&fit=crop&q=80"
              alt="Team collaborating on AI-driven digital transformation in a modern tech workspace"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            {/* Subtle left-edge gradient fade into surface */}
            <div
              className="absolute inset-y-0 left-0 w-20 pointer-events-none"
              style={{ background: 'linear-gradient(to right, #15151E, transparent)' }}
            />
            {/* Bottom gradient */}
            <div
              className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
              style={{ background: 'linear-gradient(to top, rgba(21,21,30,0.5), transparent)' }}
            />

            {/* ROI Glass Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="absolute bottom-6 left-6 mercury-glass rounded-2xl px-4 py-3 flex items-center gap-3"
              aria-label="340% Average ROI, Cross-Channel"
            >
              <div
                className="flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #FF5252, #FF8A65)' }}
              >
                <TrendingUp size={18} color="#fff" strokeWidth={2} />
              </div>
              <div>
                <div className="text-sm font-semibold leading-tight" style={{ color: '#FAFAFC' }}>
                  340% Avg. ROI
                </div>
                <div className="text-xs tracking-widest uppercase" style={{ color: '#8A8C99' }}>
                  Cross-Channel
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Digital Marketing Section - Apple Style: Clean Cards */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-gradient-to-br from-yellow-50/30 via-orange-50/30 to-pink-50/30 min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-4 tracking-tight">
              <span className="font-semibold">Digital Marketing</span><br />
              Solutions
            </h2>
            <p className="text-xl font-light text-gray-600 max-w-2xl mx-auto">
              Amplify your online presence with strategies tailored to your business
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-10 rounded-2xl hover:shadow-xl transition-all duration-300 border border-gray-100 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-100/50 to-orange-100/50 rounded-full blur-2xl -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500" />
              <div className="relative z-10">
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-2xl mb-4">
                    <FaRocket className="text-orange-400" size={28} />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Search & Social</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                From <Link to="/services/search-engine-optimization" className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">SEO</Link> that improves organic rankings to performance-driven <Link to="/services/google-ads" className="bg-gradient-to-r from-orange-300 to-pink-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">Google Ads</Link> and <Link to="/services/facebook-ads" className="bg-gradient-to-r from-pink-300 to-purple-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">Facebook Ads</Link> campaigns, we create strategies that drive measurable results.
              </p>
                <Link to="/services" className="bg-gradient-to-r from-orange-300 to-pink-300 bg-clip-text text-transparent font-medium hover:opacity-80 transition-opacity inline-flex items-center gap-2">
                  Explore services →
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-10 rounded-2xl hover:shadow-xl transition-all duration-300 border border-gray-100 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-pink-100/50 to-purple-100/50 rounded-full blur-2xl -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500" />
              <div className="relative z-10">
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl mb-4">
                    <FaChartLine className="text-pink-400" size={28} />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Marketing Automation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our <Link to="/services/email-marketing" className="bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">email marketing</Link>, <Link to="/services/sms-marketing" className="bg-gradient-to-r from-pink-300 to-purple-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">SMS</Link>, and <Link to="/services/whatsapp-marketing" className="bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">WhatsApp marketing</Link> services leverage automation and personalization to nurture leads and drive conversions across all channels.
              </p>
                <Link to="/services" className="bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent font-medium hover:opacity-80 transition-opacity inline-flex items-center gap-2">
                  Learn more →
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Software Development Section - Apple Style: Full Width Split */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-white min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-6 tracking-tight">
                Software<br />
                <span className="font-semibold">Development</span>
              </h2>
              <p className="text-xl font-light text-gray-600 mb-8 leading-relaxed">
                Our <Link to="/services/software-development" className="bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">custom software solutions</Link> are built to scale, whether you need enterprise-grade applications, mobile apps, or cloud-based platforms. We follow agile methodologies to ensure robust, secure, and maintainable code.
              </p>
              <p className="text-xl font-light text-gray-600 mb-8 leading-relaxed">
                Our <Link to="/services/ui-ux-design" className="bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">UI/UX design services</Link> combine aesthetic appeal with functional excellence, creating interfaces that users love to interact with.
              </p>
              <Link to="/services/software-development" className="bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300 bg-clip-text text-transparent font-medium hover:opacity-80 transition-opacity inline-flex items-center gap-2 text-lg">
                View services →
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative rounded-3xl overflow-hidden shadow-2xl group"
            >
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
                  alt="Software development and coding workspace with modern technology"
                  className="w-full h-full object-cover aspect-square group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute w-32 h-32 border-4 border-white/20 rounded-full"
                />
                <motion.div
                  animate={{ rotate: [360, 0] }}
                  transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                  className="absolute w-24 h-24 border-4 border-white/30 rounded-full"
                />
                <div className="relative z-10 text-center p-12">
                  <div className="mb-6">
                    <FaCode className="text-white mx-auto" size={64} />
                  </div>
                  <div className="text-3xl font-semibold text-white mb-2">Enterprise Solutions</div>
                  <div className="text-white/80 text-sm">Scalable & Secure</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Expertise - Apple Style: Large Typography, Minimal Grid */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-gradient-to-br from-purple-50/30 via-pink-50/30 to-blue-50/30 min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-4 tracking-tight">
              <span className="font-semibold">Industry</span><br />
              Expertise
            </h2>
            <p className="text-xl font-light text-gray-600 max-w-2xl mx-auto">
              Solutions tailored to your industry's unique challenges
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Healthcare', desc: 'HIPAA-compliant digital solutions and marketing strategies.', icon: FaHeartbeat, color: 'from-red-100 to-pink-100', iconColor: 'text-red-500' },
              { title: 'Fintech', desc: 'Secure platforms that navigate complex regulatory requirements.', icon: FaCreditCard, color: 'from-blue-100 to-cyan-100', iconColor: 'text-blue-500' },
              { title: 'Logistics', desc: 'Optimization tools and tracking systems for operational efficiency.', icon: FaTruck, color: 'from-orange-100 to-yellow-100', iconColor: 'text-orange-500' },
              { title: 'Education', desc: 'E-learning platforms and student engagement solutions.', icon: FaGraduationCap, color: 'from-purple-100 to-indigo-100', iconColor: 'text-purple-500' },
              { title: 'Real Estate', desc: 'Property management systems and virtual tour technologies.', icon: FaHome, color: 'from-green-100 to-teal-100', iconColor: 'text-green-500' },
              { title: 'E-commerce', desc: 'Optimize conversion funnels and increase customer lifetime value.', icon: FaShoppingBag, color: 'from-pink-100 to-rose-100', iconColor: 'text-pink-500' },
            ].map((industry, index) => {
              const IconComponent = industry.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl hover:shadow-xl transition-all duration-300 border border-gray-100 relative overflow-hidden group"
                >
                  <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${industry.color} rounded-full blur-2xl -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500 opacity-50`} />
                  <div className="relative z-10">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${industry.color} rounded-xl mb-4`}
                    >
                      <IconComponent className={industry.iconColor} size={24} />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{industry.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{industry.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data-Driven Section - Enhanced with Visual Elements */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-white relative overflow-hidden min-h-screen flex items-center">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-6">
                <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-100 to-blue-100 px-6 py-3 rounded-full">
                  <FaDatabase className="text-purple-500" size={20} />
                  <span className="text-sm font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    Analytics & Insights
                  </span>
                </div>
              </div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-8 tracking-tight">
                <span className="font-semibold">Data-Driven</span><br />
                Strategies
              </h2>
              <p className="text-xl font-light text-gray-600 mb-8 leading-relaxed">
                Every campaign, feature, and design choice is backed by comprehensive analytics and market research. We set clear KPIs, track performance metrics in real-time, and continuously optimize to ensure maximum ROI and sustainable growth.
              </p>
              <div className="space-y-4">
                {['Real-time Analytics', 'Performance Tracking', 'ROI Optimization'].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-2 h-2 bg-gradient-to-r from-purple-300 to-blue-300 rounded-full" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
              <p className="text-lg font-light text-gray-600 leading-relaxed mt-8">
                Our <Link to="/services/content-marketing" className="bg-gradient-to-r from-pink-300 via-purple-300 to-blue-300 bg-clip-text text-transparent hover:opacity-80 transition-opacity font-medium">content marketing services</Link> combine SEO best practices with engaging storytelling to attract and convert your target audience.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop"
                  alt="Data analytics dashboard showing business metrics and performance insights"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              {/* Floating Chart Icon */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-6 -right-6 bg-white rounded-2xl p-4 shadow-xl border border-gray-100"
              >
                <FaChartLine className="text-purple-500" size={32} />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partnering Section - Apple Style: Minimal Cards */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-gradient-to-br from-yellow-50/20 via-orange-50/20 to-pink-50/20 min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-4 tracking-tight">
              <span className="font-semibold">Long-Term</span><br />
              Partnerships
            </h2>
            <p className="text-xl font-light text-gray-600 max-w-2xl mx-auto">
              Beyond project delivery, we provide ongoing support and strategic guidance
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              { title: 'Startups', desc: 'Establish your digital presence with flexible, scalable solutions.', icon: FaRocket, gradient: 'from-yellow-400 to-orange-400' },
              { title: 'Mid-Size', desc: 'Scale operations with proven methodologies and dedicated support.', icon: FaChartLine, gradient: 'from-orange-400 to-pink-400' },
              { title: 'Enterprise', desc: 'Transform legacy systems with enterprise-grade solutions.', icon: FaAward, gradient: 'from-pink-400 to-purple-400' },
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-10 rounded-2xl text-center border border-gray-100 hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
                >
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${item.gradient}`} />
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="mb-6 flex justify-center"
                  >
                    <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl`}>
                      <IconComponent className="text-white" size={36} />
                    </div>
                  </motion.div>
                  <div className="text-3xl font-semibold text-gray-900 mb-4">{item.title}</div>
                  <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white p-12 rounded-2xl text-center"
          >
            <p className="text-lg font-light text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              We believe in building long-term partnerships. Our dedicated account managers work closely with you to understand evolving needs, with regular performance reviews and transparent reporting to keep you informed and in control.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['Dedicated Support', 'Performance Reviews', 'Transparent Reporting', 'Strategic Guidance'].map((item, i) => (
                <span key={i} className="px-6 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-light">
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA - Enhanced with Visual Elements */}
      <section className="py-16 sm:py-20 px-6 sm:px-8 lg:px-12 bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50 relative overflow-hidden min-h-screen flex items-center">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-orange-200/30 to-pink-200/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-purple-200/30 to-blue-200/30 rounded-full blur-3xl"
          />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="inline-block mb-8"
            >
              <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-pink-100 rounded-full flex items-center justify-center">
                <FaRocket className="text-orange-400" size={40} />
              </div>
            </motion.div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-8 tracking-tight">
              Ready to Transform<br />
              <span className="font-semibold">Your Business?</span>
            </h2>
            <p className="text-xl font-light text-gray-600 mb-12 leading-relaxed max-w-2xl mx-auto">
              Let's discuss how our AI and digital transformation solutions can drive growth for your business.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/contact"
                className="inline-block text-lg px-10 py-4 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white rounded-full font-medium hover:opacity-90 transition-opacity duration-200 shadow-xl"
              >
                Schedule a Consultation
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HomepageContent;
